/* KLIK motion (see klik-motion.css): marks content below the first screen so it rises into place as it reaches the
   screen, splits the homepage section headings into words, names a product card's photo so the browser can morph it
   into the product page (the receiving side is an inline script in the layout head, cart.py), runs the smart header
   and the navigation progress line. Content already on screen at load is never hidden.
   v2 (design-refine, 2026-10-21): calm motion (DESIGN-GUIDE 2.6): no reveal on grids, prices, the buy box, forms or
   the footer; word masks only on the homepage section headings; "below the first screen" comes from the
   IntersectionObserver's first callback, so marking reads no layout (PF-13, K-047). */
(() => {
  if (window.__klikMotion) return; // one run per page, whatever includes it
  window.__klikMotion = true;
  const root = document.documentElement;
  // Page transitions (klik-motion.css @view-transition) end at pages that do not take part — Shopify's checkout — and
  // the browser then cancels them with an unhandled error ("Transition was aborted because of invalid state"; seen in
  // the 2026-10-13 audit on the way to checkout). Going to checkout skips the transition; any cancel is handled.
  const quiet = (vt) => vt && ["ready", "finished", "updateCallbackDone"].forEach((k) => vt[k]?.catch?.(() => {}));
  addEventListener("pageswap", (e) => {
    const vt = e.viewTransition;
    if (!vt) return;
    quiet(vt);
    const to = e.activation?.entry?.url || "";
    if (/\/(checkouts?|cart\/c|account)\b/.test(to)) vt.skipTransition();
  });
  addEventListener("pagereveal", (e) => quiet(e.viewTransition));
  if (matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;
  /* fast-convert RUN (30 Oct 2026, page budget): the homepage hero's intro (the words rise in, the photo settles) plays only
     when this runs before the first paint. On a slow phone the hero is already on screen when the deferred scripts run
     (first paint ~1.2 s, this script ~3-4 s at CPU x4), and the intro made the heading and "Find your sofa" vanish and fade
     back in (main button visible again only at ~3 s): content already on screen is never hidden (see the top). */
  let painted = false;
  try { painted = performance.getEntriesByType("paint").some((p) => p.name === "first-contentful-paint"); } catch (e) {}
  if (painted) for (const el of document.querySelectorAll(".pm-hero-copy > *, .pm-hero-media img")) el.style.animation = "none";
  root.classList.add("km-on");

  /* ---------- rise into place ---------- */
  // blocks below the first screen, by page family; the homepage and content pages animate themselves (pm-/pg- reveal).
  // Never product grids, prices, the buy box, forms or the footer (v2): those are simply there.
  const BLOCKS = [
    ".kc4-related", ".klik-collection-editorial > *",
    ".kpe-specifications", ".kpe-materials", ".kpe-know",
    ".kcs", ".klik-customer-stories", ".kpdp-faq", ".kpdp-related-head",
  ].join(",");
  // word masks: the homepage section headings only (plain-text headings; the hero and the phones band keep their own motion)
  const HEADINGS = ".pm .pm-h2";
  const SKIP = "[data-pm-reveal], [data-pg-reveal], .pm, .pg, dialog, .kz-on, [data-design-flow], .kp6-purchase, .kl-pdp-panel, .kflow, .kpdp-bar, .kt-header-root, form, .kc4-grid";
  const SKIP_HEADING = ".pm-hero, .pm-made, dialog, form";
  // the studios and the options app rebuild thousands of nodes: their changes never trigger a new pass
  const QUIET = "dialog, .kz-on, [data-design-flow], [data-klik-dpo-wizard], #itoris_dynamicproductoptions, .hz-studio, .kf-studio, [data-ready-configurator], [data-furniture-configurator], .kp6-purchase, .kl-pdp-panel, .kpdp-bar";
  const seen = new WeakSet();
  const waiting = new WeakSet(); // marked below the first screen, waiting to rise
  /* fast-convert RUN (fix round, RUN-VERIFY m2): a block inside a lower product band that content-visibility still skips
     while the page loads (klik-pdp.css SP-11) has no box yet, so its first measure says nothing (zero height) and it was
     left as it is: the related-designs headings no longer rose into place. It is measured again once
     klik-product-editorial.js releases its band ([data-kpe-cv-done]), as it was before the skip. */
  const CV_BAND = ["#KpeCustomerHomes", ".kpa", ".km", ".kpdp-related", ".kpdp-faq"].map((b) => "#MainContent .kpe > section" + b).join(", ");
  const cvPending = new Map();
  /* fix round 2 (RUN-VERIFY2 m-1): a block measured again at its band's release may already be on the screen, in the bottom
     strip the observer leaves out (the last 8 %): it stays as it is (it used to vanish there and rise again). Only a block
     still below the screen's bottom edge is marked to rise. */
  const late = new WeakSet();
  let cvMo = null;
  const cvLater = (el) => {
    let band = null;
    try { band = el.closest(CV_BAND); } catch (e) {}
    if (!band || band === el || band.hasAttribute("data-kpe-cv-done")) return false;
    let list = cvPending.get(band);
    if (!list) {
      cvPending.set(band, (list = []));
      if (!cvMo) cvMo = new MutationObserver((records) => {
        for (const rec of records) {
          const done = cvPending.get(rec.target);
          if (!done || !rec.target.hasAttribute("data-kpe-cv-done")) continue;
          cvPending.delete(rec.target);
          for (const x of done) { late.add(x); io.observe(x); }
        }
      });
      cvMo.observe(band, { attributes: true, attributeFilter: ["data-kpe-cv-done"] });
    }
    list.push(el);
    return true;
  };
  // One observer does both jobs. Its first callback for a target says where the target was when it was observed
  // (entry.boundingClientRect is measured by the browser, so this script never forces a layout); later callbacks
  // reveal what was marked.
  const io = new IntersectionObserver(
    (entries) => {
      // wave1 pdp SP-03: the observer's own root (the viewport less the -8 % bottom margin) gives the limit; reading
      // innerHeight here forced a layout of the whole page inside the first callback (0.26-0.29 s at CPU x4)
      const rb = entries[0] && entries[0].rootBounds;
      const limit = rb ? rb.bottom : innerHeight * 0.92;
      const rows = new Map();
      for (const e of entries) {
        const el = e.target;
        if (waiting.has(el)) {
          if (!e.isIntersecting) continue;
          el.classList.add("km-in");
          waiting.delete(el);
          io.unobserve(el);
          continue;
        }
        const r = e.boundingClientRect;
        if (!r.height && !e.isIntersecting && cvLater(el)) { io.unobserve(el); continue; } // not laid out yet: measured on release
        // on (or above) the first screen: stays as it is; for a block measured at its band's release, the whole screen
        // (the root's bottom less the -8 % margin, scaled back: no layout read)
        const edge = late.has(el) ? (rb ? rb.top + (rb.bottom - rb.top) / 0.92 : innerHeight) : limit;
        if (e.isIntersecting || r.top < edge || !r.height) { io.unobserve(el); continue; }
        if (el.matches(HEADINGS)) {
          if (words(el)) waiting.add(el);
          else io.unobserve(el);
          continue;
        }
        // items sharing a top edge (a row) come in one after another
        const row = Math.round(r.top);
        const n = rows.get(row) || 0;
        rows.set(row, n + 1);
        el.style.setProperty("--km-d", Math.min(n, 5) * 0.06 + "s");
        el.classList.add("km-r");
        waiting.add(el);
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
  );
  function words(h) {
    if (h.children.length || !h.textContent.trim()) return false; // plain text only, links and spans stay as they are
    const parts = h.textContent.trim().split(/\s+/);
    h.setAttribute("aria-label", parts.join(" "));
    h.textContent = "";
    parts.forEach((w, i) => {
      const outer = document.createElement("span"), inner = document.createElement("span");
      outer.className = "km-w";
      outer.setAttribute("aria-hidden", "true");
      outer.style.setProperty("--km-i", i);
      inner.textContent = w;
      outer.append(inner);
      h.append(outer, i < parts.length - 1 ? " " : "");
    });
    h.classList.add("km-words");
    return true;
  }
  function mark(scope) {
    for (const el of scope.querySelectorAll(BLOCKS)) {
      if (seen.has(el) || el.closest(SKIP)) continue;
      seen.add(el);
      io.observe(el);
    }
    for (const h of scope.querySelectorAll(HEADINGS)) {
      if (seen.has(h) || h.closest(SKIP_HEADING)) continue;
      seen.add(h);
      io.observe(h);
    }
  }
  const start = () => {
    const main = document.getElementById("MainContent") || document.body;
    mark(document);
    // content added later (Show more, related designs, lazy sections) is marked as it arrives
    let timer = 0;
    new MutationObserver((records) => {
      if (timer) return;
      const outside = records.some((r) => {
        if (!r.addedNodes.length) return false;
        const el = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        return !!el && !el.closest(QUIET);
      });
      if (outside) timer = setTimeout(() => ((timer = 0), mark(main)), 200);
    }).observe(main, { childList: true, subtree: true });
  };
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", start, { once: true }) : start();

  /* ---------- the header steps aside while reading, comes back on the way up ---------- */
  const head = document.querySelector(".kt-header-root")?.closest(".shopify-section");
  if (head) {
    head.classList.add("km-head");
    // wave1 pdp SP-03: the first scroll frame records the position (reading scrollY while the deferred scripts ran forced
    // a full style and layout pass on every page, 0.1-0.24 s at CPU x4)
    let last = -1, away = false, ticking = false;
    const busy = () => !!head.querySelector("dialog[open], [aria-expanded='true']") || head.contains(document.activeElement) || document.body.style.overflow === "hidden";
    addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const y = scrollY;
          if (last < 0) last = y;
          const dy = y - last;
          head.classList.toggle("km-scrolled", y > 40);
          if (Math.abs(dy) < 8) return;
          const next = dy > 0 && y > 260 && !busy();
          if (next !== away) head.classList.toggle("km-away", (away = next));
          last = y;
        });
      },
      { passive: true },
    );
  }

  /* ---------- progress line while the next page loads; a card's photo carries over to its product page ---------- */
  /* fast-convert RUN (2026-10-30, page budget: a tap answered within 200 ms): the line is put in the page once, in idle
     time after load, and a tap only switches its class on. The tap used to append it to <body> and read offsetWidth to
     restart the animation: a forced style and layout of the whole page inside the tap (16-20 ms of script plus a
     150-190 ms style pass before the next paint at CPU x4, home EN/AR). A second tap restarts it in the next frame. */
  const bar = document.createElement("div");
  bar.className = "km-progress";
  bar.setAttribute("aria-hidden", "true");
  const place = () => { if (!bar.isConnected && document.body) document.body.append(bar); };
  const idle = (fn) => (typeof requestIdleCallback === "function" ? requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1200));
  document.readyState === "complete" ? idle(place) : addEventListener("load", () => idle(place), { once: true });
  let named = null, stop = 0, restart = 0;
  const clearName = () => named && (named.style.viewTransitionName = "", (named = null));
  document.addEventListener("click", (e) => {
    const a = e.target.closest?.("a[href]");
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || (a.target && a.target !== "_self") || a.hasAttribute("download")) return;
    let url;
    try {
      url = new URL(a.href, location.href);
    } catch {
      return;
    }
    if (url.origin !== location.origin || (url.pathname === location.pathname && url.search === location.search)) return;
    place();
    cancelAnimationFrame(restart);
    if (bar.classList.contains("km-go")) {
      bar.classList.remove("km-go");
      restart = requestAnimationFrame(() => (restart = requestAnimationFrame(() => bar.classList.add("km-go"))));
    } else bar.classList.add("km-go");
    clearTimeout(stop);
    stop = setTimeout(() => bar.classList.remove("km-go"), 9000);
    clearName();
    const img = a.closest(".kc4-card")?.querySelector("img");
    if (img && /\/products\//.test(url.pathname) && "startViewTransition" in document) {
      img.style.viewTransitionName = "km-photo";
      named = img;
      try {
        sessionStorage.setItem("km-photo", url.pathname);
      } catch {}
    }
  });
  addEventListener("pageshow", (e) => {
    if (!e.persisted) return;
    bar.classList.remove("km-go");
    clearName();
  });
})();
